import type { DisplayTemplateAst } from '#layers/data-model/types/display-template';
import type { GlobalFieldId } from '#layers/data-model/types/schema/ids';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { QuerySchemaWalker } from '#layers/data-model/utils/schema/query-schema';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { DisplayTemplateAstType } from '#layers/data-model/types/display-template';
import { resolveCollectionDisplayTemplate } from '#layers/data-model/utils/display-template';
import { getFieldDisplayBlockOptions } from '#layers/data-model/utils/fields/field-display';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { isSingleRelationField } from '#layers/data-model/utils/schema/collection';
import { CollectionReads } from '#layers/data-model/utils/schema/read-output';

/**
 * Resolve a display template AST to a plain text string by extracting field values from an item.
 *
 * Useful where only a string is accepted (e.g. Schedule-X calendar event titles).
 * For rich rendering, use the `RenderDisplayTemplate` component instead.
 */
export function resolveDisplayTemplatePlainText(
	ast: DisplayTemplateAst | null | undefined,
	item: AnyItem,
	collection: ResolvedMonospaceCollection,
	querySchema: QuerySchemaWalker | null | undefined,
): string | null {
	if (!ast?.content?.length) return null;

	const parts: string[] = [];

	for (const node of ast.content) {
		if (node.type === DisplayTemplateAstType.Text) {
			parts.push(node.text);
		}
		else if (node.type === DisplayTemplateAstType.Ref) {
			const fieldIds = Array.isArray(node.attrs.fieldId) ? node.attrs.fieldId : [node.attrs.fieldId];
			const text = toText(resolveFieldPath(fieldIds, item, collection, querySchema));

			if (text != null) {
				parts.push(text);
			}
		}
	}

	const result = parts.join('').trim();
	return result.length > 0 ? result : null;
}

/** `String()` throws for a value without a callable `toString` or `valueOf`, such as a JSON object with a `toString` key. */
function toText(value: unknown): string | null {
	if (value == null) return null;

	try {
		return String(value);
	}
	catch {
		return null;
	}
}

function resolveFieldPath(
	fieldIds: GlobalFieldId[],
	item: AnyItem,
	collection: ResolvedMonospaceCollection,
	querySchema: QuerySchemaWalker | null | undefined,
): unknown {
	if (fieldIds.length === 0 || !item) return null;

	const [firstId, ...rest] = fieldIds;
	const field = findCollectionFieldById(collection, firstId!);

	if (!field) return null;

	const value = item[field.apiName];

	if (!isSingleRelationField(field)) return rest.length === 0 ? value : null;

	// A to-many value is a list of rows, which one line of text does not summarize.
	if (field.isList || !field.oppositeCollection || value == null || typeof value !== 'object') return null;

	const relatedCollection = field.oppositeCollection as ResolvedMonospaceCollection;

	if (rest.length > 0) return resolveFieldPath(rest, value as AnyItem, relatedCollection, querySchema);

	// Expand only relations the rich renderer shows as related values: a file thumbnail has no text, and a file's `$thumbnail` is the file itself.
	if (getFieldDisplayBlockOptions(field).block !== BuiltinBlockIds.DisplayRelatedValues) return null;

	// Show the related row through the display template its field selection fetched.
	const relatedTemplate = resolveCollectionDisplayTemplate(relatedCollection, CollectionReads.Many, querySchema);
	return resolveDisplayTemplatePlainText(relatedTemplate, value as AnyItem, relatedCollection, querySchema);
}
